import { isArray, memoize } from 'lodash';
import traverse, { TraverseContext } from 'traverse';

// Use dot notation when key is too generic and can cause unwanted side effects for other content.
const skipDebugContentKeys = [
  'measurementValueUnits',
  'identifier',
  'questionnaireCode',
  'role',
  'country',
  'patient_medication_status',
  'visit_id',
  'dosage_instruction_code',
  'medication_code',
  'request_code',
  'schedule_code',
  'version',
  'study_status.*.study_defined_statuses.*.status.*.status_values.*.status_value.value',
  'status_type',
  'consent_type',
  'url',
  'group_key',
  'consent_video.*.app_and_web_attributes.*.web.date',
  'consent_video.*.video.video.url',
  'global_code.*.languages.*.code',
  'device_specification.*.table_columns.*.column_key',
  'request_tags.*.tag_groups.*.tag.*.code',
  'device_specification.*.table_units.*.value_name',
  'device_specification.*.table_units.*.units.*.unit_key',
  'device_specification.*.units.*.unit_key',
  'device_measurement.*.context.*.context.custom_context_type',
  'device_measurement.*.context.*.context.context_type',
];

/**
 * Returns true if the current key matches an entry in skipDebugContentKeys.
 * Also returns true if the current node that's being traversed is an array and the name of the array matches an entry in skipDebugContentKeys
 * @param traverseContext
 */
const shouldSkipDebugContent = (traverseContext: TraverseContext): boolean => {
  // Skip root node
  if (!traverseContext.key) {
    return false;
  }

  // Key is in list of skippable keys
  if (skipDebugContentKeys.includes(traverseContext.key)) {
    return true;
  }

  if (traverseContext.parent && traverseContext.parent.key) {
    // Skip objects in array where parent key is in list of skippable keys
    if (isArray(traverseContext.parent.node) && skipDebugContentKeys.includes(traverseContext.parent.key)) {
      return true;
    }

    // Skip keys with dot notation in list of skippable keys
    const fullPath = traverseContext.path.map(section => (isNaN(Number(section)) ? section : '*')).join('.');
    if (skipDebugContentKeys.includes(fullPath)) {
      return true;
    }
  }

  // Skip nodes that are emptry arrays to prevent null pointers where we try to iterate
  // This is needed for when content is not yet migrated to contentstack
  if (isArray(traverseContext.node) && traverseContext.node.length === 0) {
    return true;
  }

  return false;
};

/**
 * Used to create CMS debug content from real data, for debugging purposes.
 *
 * @param content a content instance (or mack content) that should be transformed
 * @param prefix name of the content type
 * @param cmsType name of the CMS which the content is fetched from, defaults to [CMS] (Cloud CMS)
 */
export const toDebugContent = <T>(content: unknown, prefix: string, cmsType = '[☁️]'): T =>
  traverse(content).map(function () {
    if (this.isLeaf) {
      // Skip content properties used for filtering, to prevent errors in runtime
      if (shouldSkipDebugContent(this)) {
        return;
      }
      this.update(getCmsDebugPath(cmsType, prefix, this.path.join('.')));
    }
  });

export const memoizedToDebugContent: typeof toDebugContent = memoize(toDebugContent, res => res);

export const getCmsDebugPath = (cmsType: string, prefix: string, path: string): string =>
  `${cmsType} ${prefix} > ${path}`;
